// @ts-nocheck
import React from 'react';

import { Analytics } from '@cobliteam/analytics';

import Icon from '@cobliteam/ui/icons';

import { applyCustomStyles } from '../../styles';
import { CaptionError } from '../../typography';
import { DefaultInput, type InputProps } from '../DefaultInput';

declare global {
  interface Window {
    google: any;
  }
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export interface GoogleAutocompleteProps extends InputProps {
  bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
  restrictions?: google.maps.places.ComponentRestrictions;
  initialValue?: string;
  onClearButtonClick?: () => void;
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
  onInputChange?: (formattedAddress: string) => void;
  underlined?: boolean;
  hasClearButton?: boolean;
  optimizedAutocomplete?: boolean;
  errorMessage?: string;
}

export interface GoogleAutocompleteState {
  value: string;
}

const CustomInput = applyCustomStyles(DefaultInput);

export class GoogleAutocompleteInput extends React.Component<GoogleAutocompleteProps, GoogleAutocompleteState> {
  private timerHandler: NodeJS.Timer | null;

  private autocomplete: google.maps.places.Autocomplete | null;

  private inputRef: React.RefObject<HTMLInputElement> | null;

  constructor(props: GoogleAutocompleteProps) {
    super(props);
    this.timerHandler = null;
    this.autocomplete = null;
    this.inputRef = React.createRef<HTMLInputElement>();
    this.state = {
      value: (this.props.initialValue as string) || (this.props.value as string) || '',
    };
  }

  private googleReadyCallback = () => {
    if (window.google) {
      if (this.timerHandler) {
        clearInterval(this.timerHandler);
      }
      this.initAutocomplete();
    }
  };

  public componentDidMount() {
    if (window.google) {
      this.initAutocomplete();
    } else {
      this.timerHandler = setInterval(this.googleReadyCallback, 1000);
    }
  }

  public componentDidUpdate(previousProps: GoogleAutocompleteProps) {
    if (this.props.initialValue !== previousProps.initialValue) {
      this.setState({ value: this.props.initialValue || '' });
    }

    if (this.props.value !== undefined && this.props.value !== this.state.value) {
      this.setState({ value: this.props.value as string });
    }
  }

  private initAutocomplete() {
    const { inputRef } = this;
    const { bounds, restrictions } = this.props;

    const autocompleteOptions = {
      types: ['geocode', 'establishment'],
      fields: ['formatted_address', 'geometry', 'address_components'],
      bounds,
    };

    if (inputRef?.current) {
      this.autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, autocompleteOptions);
      if (this.autocomplete) {
        this.autocomplete.addListener('place_changed', this.handlePlaceSelect);

        if (restrictions) {
          this.autocomplete.setComponentRestrictions(restrictions);
        }
      }
    }
  }

  public componentWillUnmount() {
    const { inputRef } = this;
    if (this.autocomplete !== null && inputRef && inputRef.current) {
      window.google.maps.event.clearInstanceListeners(inputRef.current);
    }

    if (this.timerHandler) {
      clearInterval(this.timerHandler);
    }
  }

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ value: event.currentTarget.value });

    if (this.props.onChange) {
      this.props.onChange(event);
    }

    if (this.props.onInputChange) {
      this.props.onInputChange(event.currentTarget.value);
    }
  };

  private handlePlaceSelect = () => {
    if (this.autocomplete) {
      Analytics.track('Legacy Google Autocomplete Address Selected');
      const place = this.autocomplete.getPlace();

      this.props.onPlaceSelect(place);

      if (place.formatted_address) {
        this.setState({ value: place.formatted_address || '' });
      }
    }
  };

  /*
   * This is necessary due to a bug on the input adornment click
   * https://github.com/mui-org/material-ui/issues/16665
   */
  private onMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  private handlePlaceClear = () => {
    if (this.autocomplete) {
      this.autocomplete.set('place', {});
      this.setState({ value: '' });
      if (this.props.onClearButtonClick) {
        this.props.onClearButtonClick();
      }
    }

    // This is "closing" the autocomplete options when clearing the input
    const autocompleteOptions = document.getElementsByClassName('pac-container').item(0);

    if (autocompleteOptions) {
      autocompleteOptions.setAttribute('style', 'display: none');
    }
  };

  public render() {
    const { bounds, onPlaceSelect, underlined, hasClearButton, ref, defaultValue, optimizedAutocomplete, initialValue, onInputChange, errorMessage, error, ...inputProps } =
      this.props;
    const { value } = this.state;
    const customStyles = underlined ? styles.underlinedInput : {};

    return optimizedAutocomplete ? (
      <div className={styles.inputArea}>
        <div className={errorMessage ? styles.optimizedInputError : styles.optimizedInput}>
          <input {...inputProps} value={value} onChange={this.handleInputChange} ref={this.inputRef} />
          {hasClearButton && value && (
            <ClearButton disableRipple onMouseDown={this.onMouseDown} onClick={this.handlePlaceClear}>
              <Icon name='circle-x' />
            </ClearButton>
          )}
        </div>
        {errorMessage && <CaptionError>{errorMessage}</CaptionError>}
      </div>
    ) : (
      <CustomInput
        {...inputProps}
        inputRef={this.inputRef}
        className={styles.input}
        customStyles={customStyles}
        value={value}
        ref={ref}
        error={error}
        onChange={this.handleInputChange}
        defaultValue={defaultValue}
        endAdornment={
          hasClearButton && value ? (
            <ClearButton disableRipple onMouseDown={this.onMouseDown} onClick={this.handlePlaceClear}>
              <Icon name='circle-check' />
            </ClearButton>
          ) : null
        }
      />
    );
  }
}
